"use client";

import React, { useState, useEffect } from 'react';
import { Block } from '../../interfaces.ts';
import TemplateSettingsDialog from './TemplateSettingsDialog.tsx';
import {ClientSideEditor as LexicalEditor} from '@inscript/lexical-editor';
import LocaleFieldWrapper from '../../components/LocaleFieldWrapper.tsx';
import {useLocale} from '../../context/localeContext.tsx';

interface TextBlockTemplateSettingsDialogProps {
  block: Block;
  onClose: () => void;
  onSave: (updatedBlock: Block) => void;
  availableLocales: string[];
  fallbackLocale?: string;
}

const TextBlockTemplateSettingsDialog: React.FC<TextBlockTemplateSettingsDialogProps> = ({
  block,
  onClose,
  onSave,
  availableLocales,
}) => {
  const { activeLocale } = useLocale();
  
  // Local state for content and properties (not part of context)
  const [localeContent, setLocaleContent] = useState<{
    [key: string]: { headerText: string; content: string|object };
  }>(
    availableLocales.reduce((acc: any, locale: string) => {
      acc[locale] = {
        headerText: block.defaultContent?.[locale]?.headerText || '',
        content: block.defaultContent?.[locale]?.content || '',
      };
      return acc;
    }, {})
  );

  const [withHeader, setWithHeader] = useState(block.properties?.withHeader || false);
  const [headerLevel, setHeaderLevel] = useState(block.properties?.headerLevel || 2);
  const [placeholder, setPlaceholder] = useState(block.properties?.placeholder || '');

  useEffect(() => {
    const currentContent = localeContent[activeLocale];
    if (!currentContent) {
      setLocaleContent((prev) => ({
        ...prev,
        [activeLocale]: { headerText: '', content: '' },
      }));
    }
  }, [activeLocale, localeContent]);

  const handleSave = () => {
    onSave({
      ...block,
      properties: {
        ...block.properties,
        withHeader,
        headerLevel,
        placeholder,
      },
      defaultContent: localeContent,
    });
  };

  const handleHeaderTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocaleContent((prev) => ({
      ...prev,
      [activeLocale]: { ...prev[activeLocale], headerText: e.target.value },
    }));
  };

  const handleContentChange = (value: string|object) => {
    setLocaleContent((prev) => ({
      ...prev,
      [activeLocale]: { ...prev[activeLocale], content: value },
    }));
  };

  return (
    <TemplateSettingsDialog
      title="TextBlock Settings"
      onClose={onClose}
      onSave={handleSave}
    >
      <label className="block mb-2">
        <input
          type="checkbox"
          className="mr-2"
          checked={withHeader}
          onChange={(e) => setWithHeader(e.target.checked)}
        />
        With Header
      </label>
      <label className="block mb-2">
        Placeholder:
        <input
          type="text"
          className="w-full border border-solid border-gray-300 rounded mt-1 p-1"
          value={placeholder}
          onChange={(e) => setPlaceholder(e.target.value)}
        />
      </label>
      {withHeader && (
        <>
          <LocaleFieldWrapper>
            <label className="block mb-2">
              Deafult Header Text ({activeLocale.toUpperCase()}):
              <input
                type="text"
                className="w-full border border-solid border-gray-300 rounded mt-1 p-1"
                value={localeContent[activeLocale]?.headerText || ''}
                onChange={handleHeaderTextChange}
              />
            </label>
          </LocaleFieldWrapper>
          <label className="block mb-2">
            Header Level:
            <select
              className="w-full border border-solid border-gray-300 rounded mt-1 p-1"
              value={headerLevel}
              onChange={(e) => setHeaderLevel(parseInt(e.target.value))}
            >
              {[1, 2, 3, 4, 5, 6].map((level) => (
                <option key={level} value={level}>
                  H{level}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
        <LocaleFieldWrapper>
          <label className="block mb-2">
            Default Content ({activeLocale.toUpperCase()}):
            <div
              className="border border-solid border-gray-300 rounded"
            >
              <LexicalEditor
                value={localeContent[activeLocale]?.content || ''}
                onChange={handleContentChange}
                useShadowWrapper={true}
              />
            </div>
          </label>
        </LocaleFieldWrapper>
    </TemplateSettingsDialog>
  );
};

export default TextBlockTemplateSettingsDialog;